import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import type { DimensionDescriptor, DimensionsStructure } from "../../../../../../../../../api/types/svTypes.ts";
import { ReportType } from "../../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch";
import { selectReportType } from "../../../../../store/selectors/fieldsStateSelectors";
import { createUiSettingsAction } from "../../../../../store/utils/storeUtils";
import AddToChip from "../../../financialStatement/addTo/AddToChip.tsx";
import type { FieldTreeGroup } from "../fieldTree.ts";
import { createDimensionTreeGroups } from "../fieldTree.ts";
import VirtualizedFieldTree from "../VirtualizedFieldTree.tsx";
import { compactFieldItemSx } from "../compactFieldItemSx.ts";
import FieldItemContainer from "../FieldItem.tsx";
import DimensionFieldItem from "./DimensionFieldItem.tsx";

const getGroupDimensions = (group: FieldTreeGroup<DimensionDescriptor>): DimensionDescriptor[] => [
  ...group.fields,
  ...group.groups.flatMap(getGroupDimensions),
];

const withAddToChips = (group: FieldTreeGroup<DimensionDescriptor>): FieldTreeGroup<DimensionDescriptor> => ({
  ...group,
  actions: (
    <AddToChip source={{ kind: "dimensionGroup", caption: group.caption, dimensions: getGroupDimensions(group) }} />
  ),
  groups: group.groups.map(withAddToChips),
});

const DimensionsTree = (props: {
  fields: DimensionDescriptor[];
  structure: DimensionsStructure;
  disabled: boolean;
  expanded: boolean;
  visible: boolean;
}) => {
  const { fields, structure, disabled, expanded, visible } = props;
  const dispatch = useAppDispatch();
  const reportType = useSelector(selectReportType);
  const handleDragStart = useCallback(() => {
    dispatch(createUiSettingsAction("setDragSessionId", generateGuid()));
  }, [dispatch]);

  const isFinancialStatement = reportType === ReportType.FinancialStatement;
  const showAddTo = isFinancialStatement && !disabled;
  const groups = useMemo(() => {
    const treeGroups = createDimensionTreeGroups(fields, structure.groups);
    return showAddTo ? treeGroups.map(withAddToChips) : treeGroups;
  }, [fields, structure, showAddTo]);
  const renderField = useCallback(
    (field: DimensionDescriptor) => (
      <FieldItemContainer
        field={field}
        addableField={showAddTo ? undefined : field}
        onDragStart={handleDragStart}
        disabled={disabled}
        sx={isFinancialStatement ? compactFieldItemSx : undefined}
      >
        <DimensionFieldItem
          field={field}
          actions={showAddTo ? <AddToChip source={{ kind: "dimension", dimension: field }} /> : undefined}
        />
      </FieldItemContainer>
    ),
    [handleDragStart, disabled, showAddTo, isFinancialStatement]
  );
  return (
    <VirtualizedFieldTree
      key={String(expanded)}
      groups={groups}
      defaultExpanded={expanded}
      visible={visible}
      folderLayout={isFinancialStatement}
      renderField={renderField}
    />
  );
};

export default DimensionsTree;
